import React, { useState, useRef, useEffect } from 'react';
import { 
  MessageCircle, 
  Send, 
  Users, 
  Settings, 
  Smile, 
  Paperclip, 
  X, 
  Hash,
  Wifi,
  WifiOff,
  Crown,
  Shield,
  UserX,
  Megaphone,
  Eye,
  Edit,
  Trash,
  Plus,
  Minimize2,
  Maximize2,
  LogOut,
  AlertTriangle
} from 'lucide-react';
import { getAuth } from 'firebase/auth';
import io from 'socket.io-client';
import './Chatting.css';

const Chatting = ({ isOpen = true, onClose, user: propUser, userProfile: propUserProfile, isNewWindow = false }) => {
  const [selectedChannel, setSelectedChannel] = useState('general');
  const [chatrooms, setChatrooms] = useState([]);
  const [messages, setMessages] = useState({});
  const [announcements, setAnnouncements] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState('member');
  const [isSuperUser, setIsSuperUser] = useState(false);
  
  // 새 창 모드에서는 상태 관리
  const [user, setUser] = useState(propUser);
  const [userProfile, setUserProfile] = useState(propUserProfile);
  
  // 드래그 관련 상태 (팝업 모드에서만 사용)
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ 
    x: window.innerWidth / 2 - 187.5,
    y: window.innerHeight / 2 - 333.5
  });
  const [isMinimized, setIsMinimized] = useState(false);
  
  // 모달 상태들
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showAnnouncementList, setShowAnnouncementList] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);
  const chatPopupRef = useRef(null);

  // 새 창 모드에서 메시지 리스너 설정 
  useEffect(() => {
    if (isNewWindow && window.opener) {
      const handleMessage = (event) => {
        if (event.origin !== window.location.origin) return;
        
        if (event.data.type === 'INIT_CHAT') {
          setUser(event.data.user);
          setUserProfile(event.data.userProfile);
        }
      };

      window.addEventListener('message', handleMessage);
      
      // 페이지 제목 설정
      document.title = 'WorldLand Chat';
      
      // 부모 창에 준비 완료 신호 전송
      window.opener.postMessage({
        type: 'CHAT_WINDOW_READY'
      }, window.location.origin);
      
      return () => {
        window.removeEventListener('message', handleMessage);
      };
    }
  }, [isNewWindow]);

  // 새 창에서 창 닫기 처리
  const handleCloseWindow = () => {
    if (isNewWindow) {
      window.close();
    } else if (onClose) {
      onClose();
    }
  };

  // 채널명을 채팅방 ID로 매핑하는 함수
  const getChannelToRoomIdMapping = () => {
    return {
      'general': 1,
      'dev': 2,
      'trading': 3,
      'announcements': 4
    };
  };

  // 채팅방 ID를 채널명으로 변환하는 함수
  const getRoomIdToChannelMapping = () => {
    return {
      1: 'general',
      2: 'dev',
      3: 'trading',
      4: 'announcements'
    };
  };

  // 현재 선택된 채팅방 ID 가져오기
  const getCurrentChatroomId = () => {
    const mapping = getChannelToRoomIdMapping();
    return mapping[selectedChannel] || 1;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // 드래그 이벤트 핸들러 (팝업 모드에서만)
  const handleMouseDown = (e) => {
    if (isNewWindow) return; // 새 창에서는 드래그 비활성화
    
    if (e.target.closest('.chat-header-actions')) return;
    
    setIsDragging(true);
    const rect = chatPopupRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging || isNewWindow) return;
    
    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;
    
    const maxX = window.innerWidth - 375;
    const maxY = window.innerHeight - 667;
    
    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // 드래그 이벤트 리스너 등록 (팝업 모드에서만)
  useEffect(() => {
    if (isDragging && !isNewWindow) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset, isNewWindow]);

  // 창 크기 변경 시 위치 조정 (팝업 모드에서만)
  useEffect(() => {
    if (isNewWindow) return;
    
    const handleResize = () => {
      const maxX = window.innerWidth - 375;
      const maxY = window.innerHeight - 667;
      
      setPosition(prev => ({
        x: Math.max(0, Math.min(prev.x, maxX)),
        y: Math.max(0, Math.min(prev.y, maxY))
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isNewWindow]);

  // Socket.IO 연결 초기화
  useEffect(() => {
    if (user && userProfile?.nickname) {
      connectToServer();
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [user, userProfile]);

  const connectToServer = async () => {
    if (socketRef.current) return;

    setIsConnecting(true);
    
    try {
      socketRef.current = io(process.env.REACT_APP_SERVER_URL || 'http://localhost:3002', {
        transports: ['websocket', 'polling'],
        timeout: 20000,
      });

      const socket = socketRef.current;

      // 연결 이벤트
      socket.on('connect', () => {
        console.log('서버에 연결됨:', socket.id);
        setIsConnected(true);
        setIsConnecting(false);
        authenticateUser();
      });

      socket.on('disconnect', () => {
        console.log('서버 연결 해제됨');
        setIsConnected(false);
      });

      socket.on('authenticated', (data) => {
        console.log('인증 완료:', data);
        setCurrentUserRole(data.role || 'member');
        setIsSuperUser(user.email === 'gategil1149@gmail.com');
        
        loadChatrooms();
      });

      socket.on('auth_error', (error) => {
        console.error('인증 오류:', error);
        alert('인증에 실패했습니다. 다시 로그인해주세요.');
        handleCloseWindow();
      });

      socket.on('force_disconnect', (data) => {
        alert(data.reason || '다른 창에서 로그인했습니다.');
        handleCloseWindow();
      });

      // 채팅방 목록 수신
      socket.on('chatrooms_list', (chatroomsList) => {
        console.log('채팅방 목록 수신:', chatroomsList);
        setChatrooms(chatroomsList);
      });

      // 역할 업데이트
      socket.on('user_role_updated', (data) => {
        setCurrentUserRole(data.role);
        setIsSuperUser(data.isSuperUser);
      });

      // 공지사항 관련
      socket.on('chatroom_announcements', (announcements) => {
        setAnnouncements(announcements);
      });

      socket.on('announcement_created', () => {
        setShowAnnouncement(false);
        alert('공지사항이 등록되었습니다.');
      });

      socket.on('announcement_updated', () => {
        setEditingAnnouncement(null);
        setShowAnnouncement(false);
        alert('공지사항이 수정되었습니다.');
      });

      socket.on('announcement_deleted', () => {
        alert('공지사항이 삭제되었습니다.');
      });

      // 최근 메시지 수신
      socket.on('recent_messages', (recentMessages) => {
        const formattedMessages = recentMessages.map(msg => {
          const messageDate = new Date(msg.created_at);
          return {
            id: msg.id,
            user: msg.nickname,
            photoURL: msg.photo_url,
            message: msg.message,
            timestamp: `${messageDate.getMonth() + 1}/${messageDate.getDate()} ${messageDate.toLocaleTimeString('ko-KR', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            })}`,
            createdAt: msg.created_at,
            isOwn: msg.firebase_uid === user.uid,
            userId: msg.firebase_uid
          };
        });

        setMessages(prev => ({
          ...prev,
          [selectedChannel]: formattedMessages
        }));
      });

      // 새 메시지 수신
      socket.on('new_message', (message) => {
        setMessages(prev => ({
          ...prev,
          [selectedChannel]: [...(prev[selectedChannel] || []), message]
        }));
      });

      // 시스템 메시지
      socket.on('system_message', (message) => {
        const systemMessage = {
          id: Date.now(),
          user: '시스템',
          message: message.message,
          timestamp: message.timestamp,
          isOwn: false,
          isSystem: true
        };

        setMessages(prev => ({
          ...prev,
          [selectedChannel]: [...(prev[selectedChannel] || []), systemMessage]
        }));
      });

      // 온라인 사용자 목록 업데이트
      socket.on('online_users', (users) => {
        setOnlineUsers(users);
      });

      // 사용자 입장/퇴장 알림
      socket.on('user_joined', (data) => {
        const now = new Date();
        const systemMessage = {
          id: Date.now(),
          user: '시스템',
          message: data.message,
          timestamp: `${now.getMonth() + 1}/${now.getDate()} ${now.toLocaleTimeString('ko-KR', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          })}`,
          createdAt: now.toISOString(),
          isOwn: false,
          isSystem: true
        };

        setMessages(prev => ({
          ...prev,
          [selectedChannel]: [...(prev[selectedChannel] || []), systemMessage]
        }));
      });

      socket.on('user_left', (data) => {
        const now = new Date();
        const systemMessage = {
          id: Date.now(),
          user: '시스템',
          message: data.message,
          timestamp: `${now.getMonth() + 1}/${now.getDate()} ${now.toLocaleTimeString('ko-KR', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          })}`,
          createdAt: now.toISOString(),
          isOwn: false,
          isSystem: true
        };

        setMessages(prev => ({
          ...prev,
          [selectedChannel]: [...(prev[selectedChannel] || []), systemMessage]
        }));
      });

      // 관리자 액션 응답
      socket.on('kicked_from_chatroom', (data) => {
        alert(`채팅방에서 추방되었습니다: ${data.reason}`);
      });

      socket.on('user_kicked', () => {
        alert('사용자가 추방되었습니다.');
        setShowUserManagement(false);
      });

      socket.on('sub_manager_set', () => {
        alert('부 매니저가 지정되었습니다.');
        setShowUserManagement(false);
      });

      // 채팅방 탈퇴 관련
      socket.on('chatroom_left', (data) => {
        alert('채팅방에서 탈퇴했습니다.');
        setShowSettings(false);
        setSelectedChannel('general');
        loadChatrooms();
      });

      socket.on('error', (error) => {
        console.error('Socket 오류:', error);
        alert(error);
      });

    } catch (error) {
      console.error('서버 연결 오류:', error);
      setIsConnecting(false);
      alert('서버에 연결할 수 없습니다.');
    }
  };

  const authenticateUser = async () => {
    if (!socketRef.current || !user) return;

    try {
      const auth = getAuth();
      const token = await auth.currentUser.getIdToken();

      socketRef.current.emit('authenticate', {
        token,
        userProfile
      });
    } catch (error) {
      console.error('인증 토큰 가져오기 오류:', error);
    }
  };

  // 채팅방 목록 로드
  const loadChatrooms = () => {
    if (!socketRef.current || !isConnected) return;
    socketRef.current.emit('get_chatrooms');
  };

  const handleChannelChange = (channelId) => {
    if (!socketRef.current || !isConnected) return;

    setSelectedChannel(channelId);
    socketRef.current.emit('join_channel', channelId);
  };

  const handleSendMessage = () => {
    if (!socketRef.current || !isConnected || newMessage.trim() === '') return;

    socketRef.current.emit('send_message', {
      message: newMessage.trim(),
      type: 'text'
    });

    setNewMessage('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // 공지사항 작성/수정
  const handleCreateAnnouncement = (announcementData) => {
    if (!socketRef.current || !isConnected) return;

    if (editingAnnouncement) {
      socketRef.current.emit('update_announcement', {
        announcementId: editingAnnouncement.id,
        announcementData
      });
    } else {
      socketRef.current.emit('create_announcement', announcementData);
    }
  };

  // 공지사항 삭제
  const handleDeleteAnnouncement = (announcementId) => {
    if (!socketRef.current || !isConnected) return;

    setConfirmDialog({
      title: '공지사항 삭제',
      message: '정말로 이 공지사항을 삭제하시겠습니까?',
      onConfirm: () => {
        socketRef.current.emit('delete_announcement', { announcementId });
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  // 사용자 추방
  const handleKickUser = (targetUserId) => {
    if (!socketRef.current || !isConnected) return;

    setConfirmDialog({
      title: '사용자 추방',
      message: '정말로 이 사용자를 추방하시겠습니까?',
      onConfirm: () => {
        socketRef.current.emit('kick_user', { targetUserId });
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  // 부 매니저 지정
  const handleSetSubManager = (targetUserId) => {
    if (!socketRef.current || !isConnected) return;

    setConfirmDialog({
      title: '부 매니저 지정',
      message: '이 사용자를 부 매니저로 지정하시겠습니까?',
      onConfirm: () => {
        socketRef.current.emit('set_sub_manager', { targetUserId });
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  // 채팅방 탈퇴
  const handleLeaveChatroom = () => {
    if (!socketRef.current || !isConnected) return;

    const currentChatroom = chatrooms.find(room => {
      const channelMapping = getRoomIdToChannelMapping();
      return channelMapping[room.id] === selectedChannel;
    });

    const chatroomName = currentChatroom?.name || selectedChannel;

    setConfirmDialog({
      title: '채팅방 탈퇴',
      message: `정말로 "${chatroomName}" 채팅방에서 탈퇴하시겠습니까?\n\n주의: 매니저가 탈퇴하는 경우 부 매니저가 있으면 승격되고, 없으면 채팅방이 비활성화됩니다.`,
      onConfirm: () => {
        socketRef.current.emit('leave_chatroom', { 
          chatroomId: getCurrentChatroomId() 
        });
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null),
      isDestructive: true
    });
  };

  // 관리자 권한 확인
  const hasManagerPermission = () => {
    return isSuperUser || currentUserRole === 'manager' || currentUserRole === 'sub_manager';
  };

  // 최소화/복원 (팝업 모드에서만)
  const handleMinimize = () => {
    if (!isNewWindow) {
      setIsMinimized(!isMinimized);
    }
  };

  // 배경 클릭 처리
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isDragging && !isNewWindow) {
      return;
    }
  };

  // 확인 다이얼로그 컴포넌트
  const ConfirmDialog = () => {
    if (!confirmDialog) return null;

    return (
      <div className="modal-backdrop">
        <div className="confirm-dialog">
          <h3>{confirmDialog.title}</h3>
          <p style={{ whiteSpace: 'pre-line' }}>{confirmDialog.message}</p>
          <div className="confirm-actions">
            <button onClick={confirmDialog.onCancel} className="btn-cancel">
              취소
            </button>
            <button 
              onClick={confirmDialog.onConfirm} 
              className={confirmDialog.isDestructive ? "btn-confirm destructive" : "btn-confirm"}
            >
              확인
            </button>
          </div>
        </div>
      </div>
    );
  };

  // 설정 모달 컴포넌트
  const SettingsModal = () => {
    const currentChatroom = chatrooms.find(room => {
      const channelMapping = getRoomIdToChannelMapping();
      return channelMapping[room.id] === selectedChannel;
    });

    const chatroomName = currentChatroom?.name || selectedChannel;
    const canLeaveChatroom = !isSuperUser;

    return (
      <div className="modal-backdrop" onClick={() => setShowSettings(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>설정</h3>
            <button onClick={() => setShowSettings(false)}>
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="modal-body">
            <div className="settings-section">
              <h4 className="settings-section-title">채팅방 정보</h4>
              <div className="settings-info">
                <div className="info-item">
                  <span className="info-label">현재 채팅방:</span>
                  <span className="info-value">#{chatroomName}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">내 역할:</span>
                  <span className="info-value">
                    {currentUserRole}
                    {currentUserRole === 'manager' && <Crown className="w-4 h-4 text-yellow-500 ml-1 inline" />}
                    {currentUserRole === 'sub_manager' && <Shield className="w-4 h-4 text-blue-500 ml-1 inline" />}
                    {isSuperUser && <span className="superuser-badge-small ml-2">SUPER</span>}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">온라인 사용자:</span>
                  <span className="info-value">{onlineUsers.length}명</span>
                </div>
              </div>
            </div>

            <div className="settings-section">
              <h4 className="settings-section-title">채팅방 관리</h4>
              <div className="settings-actions">
                {canLeaveChatroom ? (
                  <button 
                    onClick={handleLeaveChatroom}
                    className="settings-btn danger"
                    disabled={!isConnected}
                  >
                    <LogOut className="w-4 h-4" />
                    <span>채팅방 탈퇴</span>
                  </button>
                ) : (
                  <div className="settings-warning">
                    <AlertTriangle className="w-4 h-4 text-yellow-500" />
                    <span>슈퍼유저는 채팅방을 탈퇴할 수 없습니다.</span>
                  </div>
                )}
              </div>
            </div>

            <div className="settings-section">
              <h4 className="settings-section-title">앱 정보</h4>
              <div className="settings-info">
                <div className="info-item">
                  <span className="info-label">버전:</span>
                  <span className="info-value">WorldLand Chat v2.0</span>
                </div>
                <div className="info-item">
                  <span className="info-label">연결 상태:</span>
                  <span className={`info-value ${isConnected ? 'connected' : 'disconnected'}`}>
                    {isConnected ? '연결됨' : '연결 끊김'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 공지사항 모달 컴포넌트
  const AnnouncementModal = () => {
    const [title, setTitle] = useState(editingAnnouncement?.title || '');
    const [content, setContent] = useState(editingAnnouncement?.content || '');

    const handleSubmit = (e) => {
      e.preventDefault();
      if (title.trim() && content.trim()) {
        handleCreateAnnouncement({ title: title.trim(), content: content.trim() });
      }
    };

    return (
      <div className="modal-backdrop" onClick={() => { setShowAnnouncement(false); setEditingAnnouncement(null); }}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>{editingAnnouncement ? '공지사항 수정' : '공지사항 작성'}</h3>
            <button onClick={() => { setShowAnnouncement(false); setEditingAnnouncement(null); }}>
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="modal-body">
            <div className="form-group">
              <label>제목</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="공지사항 제목을 입력하세요"
                required
              />
            </div>
            <div className="form-group">
              <label>내용</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="공지사항 내용을 입력하세요"
                rows="6"
                required
              />
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => { setShowAnnouncement(false); setEditingAnnouncement(null); }}>
                취소
              </button>
              <button type="submit">
                {editingAnnouncement ? '수정' : '작성'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // 사용자 관리 모달 컴포넌트
  const UserManagementModal = () => {
    return (
      <div className="modal-backdrop" onClick={() => setShowUserManagement(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>사용자 관리</h3>
            <button onClick={() => setShowUserManagement(false)}>
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="modal-body">
            <div className="user-list">
              {onlineUsers.map((user, index) => (
                <div key={index} className="user-item">
                  <div className="user-info">
                    <img 
                      src={user.photo_url || '/default-avatar.png'} 
                      alt={user.nickname}
                      className="user-avatar"
                    />
                    <div>
                      <div className="user-name">
                        {user.nickname}
                        {user.role === 'manager' && <Crown className="w-4 h-4 text-yellow-500 ml-1" />}
                        {user.role === 'sub_manager' && <Shield className="w-4 h-4 text-blue-500 ml-1" />}
                        {user.email === 'gategil1149@gmail.com' && <span className="superuser-badge">SUPER</span>}
                      </div>
                      <div className="user-role">{user.role}</div>
                    </div>
                  </div>
                  {hasManagerPermission() && user.email !== 'gategil1149@gmail.com' && user.firebase_uid !== user.uid && (
                    <div className="user-actions">
                      {(isSuperUser || currentUserRole === 'manager') && user.role === 'member' && (
                        <button 
                          onClick={() => handleSetSubManager(user.id)}
                          className="action-btn sub-manager"
                          title="부 매니저 지정"
                        >
                          <Shield className="w-4 h-4" />
                        </button>
                      )}
                      <button 
                        onClick={() => handleKickUser(user.id)}
                        className="action-btn kick"
                        title="사용자 추방"
                      >
                        <UserX className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 공지사항 목록 모달
  const AnnouncementListModal = () => {
    return (
      <div className="modal-backdrop" onClick={() => setShowAnnouncementList(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>공지사항 목록</h3>
            <button onClick={() => setShowAnnouncementList(false)}>
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="modal-body">
            {announcements.length === 0 ? (
              <p className="no-announcements">공지사항이 없습니다.</p>
            ) : (
              <div className="announcement-list">
                {announcements.map((announcement) => (
                  <div key={announcement.id} className="announcement-item">
                    <div className="announcement-header">
                      <h4>{announcement.title}</h4>
                      <div className="announcement-meta">
                        <span>작성자: {announcement.author_nickname}</span>
                        <span>{new Date(announcement.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="announcement-content">
                      {announcement.content}
                    </div>
                    {hasManagerPermission() && (
                      <div className="announcement-actions">
                        <button 
                          onClick={() => {
                            setEditingAnnouncement(announcement);
                            setShowAnnouncement(true);
                            setShowAnnouncementList(false);
                          }}
                          className="action-btn edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleDeleteAnnouncement(announcement.id)}
                          className="action-btn delete"
                        >
                          <Trash className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // 사용자 정보가 없으면 로딩 표시
  if (!user || !userProfile?.nickname) {
    return (
      <div className={isNewWindow ? "chat-new-window" : "chat-popup"}>
        <div className="chat-loading">
          <p>사용자 정보를 로드하는 중...</p>
        </div>
      </div>
    );
  }

  // 팝업 모드에서 닫혀있으면 렌더링하지 않음
  if (!isOpen && !isNewWindow) return null;

  // 새 창 모드인 경우 전체 화면으로 렌더링
  if (isNewWindow) {
    return (
      <div className="chat-new-window">
        <div className="chat-container">
          {/* Header */}
          <div className="chat-header">
            <div className="chat-header-info">
              <div>
                <h3 className="chat-title">WorldLand Chat</h3>
                <p className="chat-subtitle">
                  #{chatrooms.find(room => {
                    const channelMapping = getRoomIdToChannelMapping();
                    return channelMapping[room.id] === selectedChannel;
                  })?.name || selectedChannel}
                </p>
              </div>
            </div>
            <div className="chat-header-actions">
              <div className="chat-connection-status">
                {isConnecting ? (
                  <div className="chat-connecting">연결 중...</div>
                ) : isConnected ? (
                  <Wifi className="w-4 h-4 text-green-500" title="연결됨" />
                ) : (
                  <WifiOff className="w-4 h-4 text-red-500" title="연결 끊김" />
                )}
              </div>
              
              {/* 관리자 메뉴 */}
              {hasManagerPermission() && (
                <>
                  <button 
                    className="chat-action-btn"
                    onClick={() => setShowAnnouncement(true)}
                    title="공지사항 작성"
                  >
                    <Megaphone className="w-4 h-4" />
                  </button>
                  <button 
                    className="chat-action-btn"
                    onClick={() => setShowUserManagement(true)}
                    title="사용자 관리"
                  >
                    <Users className="w-4 h-4" />
                  </button>
                </>
              )}
              
              <button 
                className="chat-action-btn"
                onClick={() => setShowAnnouncementList(true)}
                title="공지사항 보기"
              >
                <Eye className="w-4 h-4" />
              </button>
              
              <button 
                className="chat-action-btn"
                onClick={() => setShowSettings(true)}
                title="설정"
              >
                <Settings className="w-4 h-4" />
              </button>
              
              <button className="chat-action-btn" onClick={handleCloseWindow}>
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="chat-body">
            {/* Sidebar */}
            <div className="chat-sidebar">
              {/* Channels */}
              <div className="chat-channels">
                {chatrooms.length > 0 ? (
                  chatrooms.map((chatroom) => {
                    const channelMapping = getRoomIdToChannelMapping();
                    const channelId = channelMapping[chatroom.id] || `room_${chatroom.id}`;
                    
                    return (
                      <button
                        key={chatroom.id}
                        onClick={() => handleChannelChange(channelId)}
                        disabled={!isConnected}
                        className={`chat-channel-btn ${selectedChannel === channelId ? 'active' : ''} ${!isConnected ? 'disabled' : ''}`}
                        title={`${chatroom.name} - ${chatroom.description || ''}`}
                      >
                        {/* <Hash className="w-3 h-3" /> */}
                        <div className="chat-channel-info">
                          <div className="chat-channel-name">{chatroom.name}</div>
                          <div className="chat-channel-members">{chatroom.current_members}명</div>
                        </div>
                        {(chatroom.manager_id && user && chatroom.manager_nickname) && (
                          <div className="chat-channel-manager">
                            {chatroom.manager_nickname === userProfile?.nickname ? (
                              <Crown className="w-3 h-3 text-yellow-500" title="내가 매니저" />
                            ) : (
                              <Crown className="w-3 h-3 text-gray-400" title={`매니저: ${chatroom.manager_nickname}`} />
                            )}
                          </div>
                        )}
                      </button>
                    );
                  })
                ) : (
                  <>
                    <button
                      onClick={() => handleChannelChange('general')}
                      disabled={!isConnected}
                      className={`chat-channel-btn ${selectedChannel === 'general' ? 'active' : ''} ${!isConnected ? 'disabled' : ''}`}
                      title="일반 채팅"
                    >
                      {/* <Hash className="w-3 h-3" /> */}
                      <div className="chat-channel-info">
                        <div className="chat-channel-name">일반</div>
                      </div>
                    </button>
                    <button
                      onClick={() => handleChannelChange('dev')}
                      disabled={!isConnected}
                      className={`chat-channel-btn ${selectedChannel === 'dev' ? 'active' : ''} ${!isConnected ? 'disabled' : ''}`}
                      title="개발 관련 채팅"
                    >
                      {/* <Hash className="w-3 h-3" /> */}
                      <div className="chat-channel-info">
                        <div className="chat-channel-name">개발</div>
                      </div>
                    </button>
                    <button
                      onClick={() => handleChannelChange('trading')}
                      disabled={!isConnected}
                      className={`chat-channel-btn ${selectedChannel === 'trading' ? 'active' : ''} ${!isConnected ? 'disabled' : ''}`}
                      title="트레이딩 관련 채팅"
                    >
                      {/* <Hash className="w-3 h-3" /> */}
                      <div className="chat-channel-info">
                        <div className="chat-channel-name">트레이딩</div>
                      </div>
                    </button>
                    <button
                      onClick={() => handleChannelChange('announcements')}
                      disabled={!isConnected}
                      className={`chat-channel-btn ${selectedChannel === 'announcements' ? 'active' : ''} ${!isConnected ? 'disabled' : ''}`}
                      title="공지사항"
                    >
                      {/* <Hash className="w-3 h-3" /> */}
                      <div className="chat-channel-info">
                        <div className="chat-channel-name">공지</div>
                      </div>
                    </button>
                  </>
                )}
              </div>

              {/* Current User */}
              <div className="chat-current-user">
                <div className="chat-user-avatar">
                  {user?.photoURL ? (
                    <img src={user.photoURL} alt="내 프로필" className="chat-user-photo" />
                  ) : (
                    <span>👤</span>
                  )}
                  <div className={`chat-user-status ${isConnected ? 'online' : 'offline'}`}></div>
                </div>
                <div className="chat-user-info">
                  <div className="chat-user-name">
                    {userProfile?.nickname}
                    {currentUserRole === 'manager' && <Crown className="w-3 h-3 text-yellow-500 ml-1" />}
                    {currentUserRole === 'sub_manager' && <Shield className="w-3 h-3 text-blue-500 ml-1" />}
                  </div>
                  <div className="chat-user-role">{currentUserRole}</div>
                </div>
              </div>

              {/* Online Users */}
              <div className="chat-users">
                <div className="chat-users-header">
                  <Users className="w-3 h-3" />
                  <span>{onlineUsers.length}</span>
                </div>
                <div className="chat-users-list">
                  {onlineUsers.slice(0, 8).map((onlineUser, index) => (
                    <div key={index} className="chat-user" title={`${onlineUser.nickname} (${onlineUser.role})`}>
                      <div className="chat-user-avatar">
                        {onlineUser.photo_url ? (
                          <img src={onlineUser.photo_url} alt={onlineUser.nickname} className="chat-user-photo" />
                        ) : (
                          <span>👤</span>
                        )}
                        <div className="chat-user-status online"></div>
                        {onlineUser.role === 'manager' && <Crown className="w-2 h-2 text-yellow-500 absolute -top-1 -right-1" />}
                        {onlineUser.role === 'sub_manager' && <Shield className="w-2 h-2 text-blue-500 absolute -top-1 -right-1" />}
                      </div>
                      <div className="chat-user-nickname">{onlineUser.nickname}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Chat Area */}
            <div className="chat-main">
              {/* Connection Status */}
              {!isConnected && (
                <div className="chat-connection-warning">
                  <WifiOff className="w-5 h-5" />
                  <span>서버와의 연결이 끊어졌습니다.</span>
                  <button 
                    onClick={connectToServer}
                    className="chat-reconnect-btn"
                    disabled={isConnecting}
                  >
                    {isConnecting ? '연결 중...' : '다시 연결'}
                  </button>
                </div>
              )}

              {/* Announcements Banner */}
              {announcements.length > 0 && (
                <div className="chat-announcements-banner">
                  <Megaphone className="w-4 h-4" />
                  <span>📢 {announcements[0].title}</span>
                  <button onClick={() => setShowAnnouncementList(true)}>
                    <Eye className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Messages */}
              <div className="chat-messages">
                {(messages[selectedChannel] || []).map((message) => (
                  <div key={message.id} className={`chat-message ${message.isOwn ? 'own' : ''} ${message.isSystem ? 'system' : ''}`}>
                    <div className="chat-message-content">
                      {!message.isOwn && !message.isSystem && (
                        <div className="chat-message-header">
                          <span className="chat-message-avatar">
                            {message.photoURL ? (
                              <img src={message.photoURL} alt="프로필" className="chat-avatar-img" />
                            ) : (
                              <span>👤</span>
                            )}
                          </span>
                          <span className="chat-message-user">{message.user}</span>
                          <span className="chat-message-time">{message.timestamp}</span>
                        </div>
                      )}
                      <div className={`chat-message-bubble ${message.isSystem ? 'system' : ''}`}>
                        <p>{message.message}</p>
                        {message.isOwn && !message.isSystem && (
                          <div className="chat-message-own-time">
                            {message.timestamp}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="chat-input-area">
                <div className="chat-input-container">
                  <div className="chat-input-wrapper">
                    <textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder={`#${chatrooms.find(room => {
                        const channelMapping = getRoomIdToChannelMapping();
                        return channelMapping[room.id] === selectedChannel;
                      })?.name || selectedChannel}에 메시지 보내기...`}
                      className="chat-input"
                      rows="1"
                      disabled={!isConnected}
                    />
                    <div className="chat-input-actions">
                      <div className="chat-input-tools">
                        <button className="chat-tool-btn" disabled={!isConnected}>
                          <Paperclip className="w-4 h-4" />
                        </button>
                        <button className="chat-tool-btn" disabled={!isConnected}>
                          <Smile className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="chat-input-hint">
                        {isConnected ? 'Enter로 전송' : '연결 대기 중'}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleSendMessage}
                    disabled={!isConnected || newMessage.trim() === ''}
                    className="chat-send-btn"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modals */}
        {showAnnouncement && <AnnouncementModal />}
        {showUserManagement && <UserManagementModal />}
        {showAnnouncementList && <AnnouncementListModal />}
        {showSettings && <SettingsModal />}
        <ConfirmDialog />
      </div>
    );
  }

  // 팝업 모드 렌더링
  return (
    <>
      <div className="chat-backdrop" onClick={handleBackdropClick}></div>
      
      <div 
        ref={chatPopupRef}
        className={`chat-popup ${isDragging ? 'dragging' : ''}`}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          height: isMinimized ? '60px' : '667px',
          transition: isDragging ? 'none' : 'all 0.3s ease',
          cursor: isDragging ? 'grabbing' : 'default'
        }}
      >
        <div className="chat-container">
          {/* Header - 드래그 핸들 */}
          <div 
            className="chat-header"
            onMouseDown={handleMouseDown}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <div className="chat-header-info">
              <div>
                {!isMinimized && (
                  <p className="chat-subtitle" style={{width: '45px'}}>
                    {chatrooms.find(room => {
                      const channelMapping = getRoomIdToChannelMapping();
                      return channelMapping[room.id] === selectedChannel;
                    })?.name || selectedChannel}
                  </p>
                )}
              </div>
            </div>
            <div className="chat-header-actions">
              <div className="chat-connection-status">
                {isConnecting ? (
                  <div className="chat-connecting">연결 중...</div>
                ) : isConnected ? (
                  <Wifi className="w-4 h-4 text-green-500" title="연결됨" />
                ) : (
                  <WifiOff className="w-4 h-4 text-red-500" title="연결 끊김" />
                )}
              </div>
              
              {!isMinimized && (
                <>
                  {/* 관리자 메뉴 */}
                  {hasManagerPermission() && (
                    <>
                      <button 
                        className="chat-action-btn"
                        onClick={() => setShowAnnouncement(true)}
                        title="공지사항 작성"
                      >
                        <Megaphone className="w-4 h-4" />
                      </button>
                      <button 
                        className="chat-action-btn"
                        onClick={() => setShowUserManagement(true)}
                        title="사용자 관리"
                      >
                        <Users className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  
                  <button 
                    className="chat-action-btn"
                    onClick={() => setShowAnnouncementList(true)}
                    title="공지사항 보기"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  
                  <button 
                    className="chat-action-btn"
                    onClick={() => setShowSettings(true)}
                    title="설정"
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                </>
              )}
              
              <button 
                className="chat-action-btn"
                onClick={handleMinimize}
                title={isMinimized ? "복원" : "최소화"}
              >
                {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
              </button>
              
              <button className="chat-action-btn" onClick={handleCloseWindow}>
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* 최소화 상태가 아닐 때만 채팅 바디 표시 */}
          {!isMinimized && (
            <div className="chat-body">
              {/* Sidebar */}
              <div className="chat-sidebar">
                {/* Channels */}
                <div className="chat-channels">
                  {chatrooms.length > 0 ? (
                    chatrooms.map((chatroom) => {
                      const channelMapping = getRoomIdToChannelMapping();
                      const channelId = channelMapping[chatroom.id] || `room_${chatroom.id}`;
                      
                      return (
                        <button
                          key={chatroom.id}
                          onClick={() => handleChannelChange(channelId)}
                          disabled={!isConnected}
                          className={`chat-channel-btn ${selectedChannel === channelId ? 'active' : ''} ${!isConnected ? 'disabled' : ''}`}
                          title={`${chatroom.name} - ${chatroom.description || ''}`}
                        >
                          {/* <Hash className="w-3 h-3" /> */}
                          <div className="chat-channel-info">
                            <div className="chat-channel-name">{chatroom.name}</div>
                            <div className="chat-channel-members">{chatroom.current_members}명</div>
                          </div>
                          {(chatroom.manager_id && user && chatroom.manager_nickname) && (
                            <div className="chat-channel-manager">
                              {chatroom.manager_nickname === userProfile?.nickname ? (
                                <Crown className="w-3 h-3 text-yellow-500" title="내가 매니저" />
                              ) : (
                                <Crown className="w-3 h-3 text-gray-400" title={`매니저: ${chatroom.manager_nickname}`} />
                              )}
                            </div>
                          )}
                        </button>
                      );
                    })
                  ) : (
                    <>
                      <button
                        onClick={() => handleChannelChange('general')}
                        disabled={!isConnected}
                        className={`chat-channel-btn ${selectedChannel === 'general' ? 'active' : ''} ${!isConnected ? 'disabled' : ''}`}
                        title="일반 채팅"
                      >
                        {/* <Hash className="w-3 h-3" /> */}
                        <div className="chat-channel-info">
                          <div className="chat-channel-name">일반</div>
                        </div>
                      </button>
                      <button
                        onClick={() => handleChannelChange('dev')}
                        disabled={!isConnected}
                        className={`chat-channel-btn ${selectedChannel === 'dev' ? 'active' : ''} ${!isConnected ? 'disabled' : ''}`}
                        title="개발 관련 채팅"
                      >
                        {/* <Hash className="w-3 h-3" /> */}
                        <div className="chat-channel-info">
                          <div className="chat-channel-name">개발</div>
                        </div>
                      </button>
                      <button
                        onClick={() => handleChannelChange('trading')}
                        disabled={!isConnected}
                        className={`chat-channel-btn ${selectedChannel === 'trading' ? 'active' : ''} ${!isConnected ? 'disabled' : ''}`}
                        title="트레이딩 관련 채팅"
                      >
                        {/* <Hash className="w-3 h-3" /> */}
                        <div className="chat-channel-info">
                          <div className="chat-channel-name">트레이딩</div>
                        </div>
                      </button>
                      <button
                        onClick={() => handleChannelChange('announcements')}
                        disabled={!isConnected}
                        className={`chat-channel-btn ${selectedChannel === 'announcements' ? 'active' : ''} ${!isConnected ? 'disabled' : ''}`}
                        title="공지사항"
                      >
                        {/* <Hash className="w-3 h-3" /> */}
                        <div className="chat-channel-info">
                          <div className="chat-channel-name">공지</div>
                        </div>
                      </button>
                    </>
                  )}
                </div>

                {/* Current User */}
                <div className="chat-current-user">
                  <div className="chat-user-avatar">
                    {user?.photoURL ? (
                      <img src={user.photoURL} alt="내 프로필" className="chat-user-photo" />
                    ) : (
                      <span>👤</span>
                    )}
                    <div className={`chat-user-status ${isConnected ? 'online' : 'offline'}`}></div>
                  </div>
                  <div className="chat-user-info">
                    <div className="chat-user-name">
                      {userProfile?.nickname}
                      {currentUserRole === 'manager' && <Crown className="w-3 h-3 text-yellow-500 ml-1" />}
                      {currentUserRole === 'sub_manager' && <Shield className="w-3 h-3 text-blue-500 ml-1" />}
                    </div>
                    <div className="chat-user-role">{currentUserRole}</div>
                  </div>
                </div>

                {/* Online Users */}
                <div className="chat-users">
                  <div className="chat-users-header">
                    <Users className="w-3 h-3" />
                    <span>{onlineUsers.length}</span>
                  </div>
                  <div className="chat-users-list">
                    {onlineUsers.slice(0, 8).map((onlineUser, index) => (
                      <div key={index} className="chat-user" title={`${onlineUser.nickname} (${onlineUser.role})`}>
                        <div className="chat-user-avatar">
                          {onlineUser.photo_url ? (
                            <img src={onlineUser.photo_url} alt={onlineUser.nickname} className="chat-user-photo" />
                          ) : (
                            <span>👤</span>
                          )}
                          <div className="chat-user-status online"></div>
                          {onlineUser.role === 'manager' && <Crown className="w-2 h-2 text-yellow-500 absolute -top-1 -right-1" />}
                          {onlineUser.role === 'sub_manager' && <Shield className="w-2 h-2 text-blue-500 absolute -top-1 -right-1" />}
                        </div>
                        <div className="chat-user-nickname">{onlineUser.nickname}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Chat Area */}
              <div className="chat-main">
                {/* Connection Status */}
                {!isConnected && (
                  <div className="chat-connection-warning">
                    <WifiOff className="w-5 h-5" />
                    <span>서버와의 연결이 끊어졌습니다.</span>
                    <button 
                      onClick={connectToServer}
                      className="chat-reconnect-btn"
                      disabled={isConnecting}
                    >
                      {isConnecting ? '연결 중...' : '다시 연결'}
                    </button>
                  </div>
                )}

                {/* Announcements Banner */}
                {announcements.length > 0 && (
                  <div className="chat-announcements-banner">
                    <Megaphone className="w-4 h-4" />
                    <span>📢 {announcements[0].title}</span>
                    <button onClick={() => setShowAnnouncementList(true)}>
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Messages */}
                <div className="chat-messages">
                  {(messages[selectedChannel] || []).map((message) => (
                    <div key={message.id} className={`chat-message ${message.isOwn ? 'own' : ''} ${message.isSystem ? 'system' : ''}`}>
                      <div className="chat-message-content">
                        {!message.isOwn && !message.isSystem && (
                          <div className="chat-message-header">
                            <span className="chat-message-avatar">
                              {message.photoURL ? (
                                <img src={message.photoURL} alt="프로필" className="chat-avatar-img" />
                              ) : (
                                <span>👤</span>
                              )}
                            </span>
                            <span className="chat-message-user">{message.user}</span>
                            <span className="chat-message-time">{message.timestamp}</span>
                          </div>
                        )}
                        <div className={`chat-message-bubble ${message.isSystem ? 'system' : ''}`}>
                          <p>{message.message}</p>
                          {message.isOwn && !message.isSystem && (
                            <div className="chat-message-own-time">
                              {message.timestamp}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <div className="chat-input-area">
                  <div className="chat-input-container">
                    <div className="chat-input-wrapper">
                      <textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder={`#${chatrooms.find(room => {
                          const channelMapping = getRoomIdToChannelMapping();
                          return channelMapping[room.id] === selectedChannel;
                        })?.name || selectedChannel}에 메시지 보내기...`}
                        className="chat-input"
                        rows="1"
                        disabled={!isConnected}
                      />
                      <div className="chat-input-actions">
                        <div className="chat-input-tools">
                          <button className="chat-tool-btn" disabled={!isConnected}>
                            <Paperclip className="w-4 h-4" />
                          </button>
                          <button className="chat-tool-btn" disabled={!isConnected}>
                            <Smile className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="chat-input-hint">
                          {isConnected ? 'Enter로 전송' : '연결 대기 중'}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={handleSendMessage}
                      disabled={!isConnected || newMessage.trim() === ''}
                      className="chat-send-btn"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showAnnouncement && <AnnouncementModal />}
      {showUserManagement && <UserManagementModal />}
      {showAnnouncementList && <AnnouncementListModal />}
      {showSettings && <SettingsModal />}
      <ConfirmDialog />
    </>
  );
};

export default Chatting;